import { t } from '../i18n/nl.js';
import { todayStr, toAmsterdamISO } from './dateUtils.js';

// Rekenlogica achter de sectie Trainingshistorie (fitness tracking deel 3).
// Bewust losse, pure functies: ze zijn zonder React te draaien en daarmee
// verifieerbaar zolang workout_sessions nog leeg is (deel 4 vult die pas).

const nlGetal = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

// PostgREST geeft numeric/decimal terug als JSON-string (hoofdstuk 8):
// altijd door Number() vóór rekenen of vergelijken.
export function num(waarde) {
  if (waarde === null || waarde === undefined || waarde === '') return null;
  const n = Number(waarde);
  return Number.isFinite(n) ? n : null;
}

export function formatGetal(waarde) {
  return nlGetal.format(waarde);
}

// Grenzen van de huidige kalendermaand in Amsterdamse tijd. Kalendermaand,
// nooit "30 dagen terug" (vaste modelregel uit BRIEFING hoofdstuk 4).
export function maandGrenzen(vandaag = todayStr()) {
  const [jaar, maand] = vandaag.split('-').map(Number);
  const volgendJaar = maand === 12 ? jaar + 1 : jaar;
  const volgendeMaand = maand === 12 ? 1 : maand + 1;
  const pad = n => String(n).padStart(2, '0');
  return {
    van: toAmsterdamISO(`${jaar}-${pad(maand)}-01`, '00:00'),
    tot: toAmsterdamISO(`${volgendJaar}-${pad(volgendeMaand)}-01`, '00:00'),
  };
}

// Dezelfde kalendermaandgrenzen, maar als kale datumstring — voor filters
// op een `date`-kolom (`workout_sessions.performed_on`). Een tijdstempel met
// tijdzone-offset zou PostgREST daar naar een datum casten en zo net over de
// maandgrens kunnen schuiven.
export function maandGrenzenAlsDatum(vandaag = todayStr()) {
  const [jaar, maand] = vandaag.split('-').map(Number);
  const volgendJaar = maand === 12 ? jaar + 1 : jaar;
  const volgendeMaand = maand === 12 ? 1 : maand + 1;
  const pad = n => String(n).padStart(2, '0');
  return {
    van: `${jaar}-${pad(maand)}-01`,
    tot: `${volgendJaar}-${pad(volgendeMaand)}-01`,
  };
}

// Oefeningen van een sessie in de vastgelegde volgorde, met hun sets op
// set_number. Sorteren gebeurt client-side, net als in ClientTemplatesPanel.
export function oefeningenVan(sessie) {
  return [...(sessie?.workout_session_exercises || [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(oefening => ({
      ...oefening,
      sets: [...(oefening.workout_sets || [])].sort((a, b) => a.set_number - b.set_number),
    }));
}

function bereik(waarden) {
  const min = Math.min(...waarden);
  const max = Math.max(...waarden);
  return min === max ? formatGetal(min) : `${formatGetal(min)}-${formatGetal(max)}`;
}

// Sets van één oefening samenvatten tot "3×10 @ 40-42,5 kg". Reps en gewicht
// worden als bereik getoond zodra ze binnen de oefening verschillen; sets
// zonder reps of zonder gewicht laten dat deel gewoon weg.
export function vatSetsSamen(sets) {
  const aantal = sets.length;
  const repsWaarden = sets.map(s => num(s.reps)).filter(v => v !== null);
  const gewichtWaarden = sets.map(s => num(s.weight)).filter(v => v !== null);

  let tekst = repsWaarden.length > 0
    ? t('trainingHistory.oefeningSets', { sets: aantal, reps: bereik(repsWaarden) })
    : t(aantal === 1 ? 'trainingHistory.oefeningSetsZonderRepsEnkel' : 'trainingHistory.oefeningSetsZonderReps', { sets: aantal });

  if (gewichtWaarden.length > 0) {
    tekst += ` @ ${t('trainingHistory.oefeningGewicht', { gewicht: bereik(gewichtWaarden) })}`;
  }
  return tekst;
}

function tijdLabel(seconden) {
  const s = Math.round(seconden);
  const uren = Math.floor(s / 3600);
  const min = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mmss = `${String(min).padStart(uren > 0 ? 2 : 1, '0')}:${String(sec).padStart(2, '0')}`;
  return uren > 0 ? `${uren}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : mmss;
}

// Sets van één cardio-oefening samenvatten, bv. "5 km · 28:30 · 6,2% helling
// · 310 kcal". Elke metric komt alleen voor zodra minstens één set een
// waarde heeft; bij meerdere sets met verschillende waarden wordt het net
// als bij kracht een bereik (bereik()-helper hierboven).
export function vatCardioSetsSamen(sets) {
  const segmenten = [];

  const afstanden = sets.map(s => num(s.distance_meters)).filter(v => v !== null);
  if (afstanden.length > 0) {
    const km = afstanden.map(m => m / 1000);
    segmenten.push(t('trainingHistory.cardioAfstand', { waarde: bereik(km) }));
  }

  const tijden = sets.map(s => num(s.duration_seconds)).filter(v => v !== null);
  if (tijden.length > 0) {
    const min = Math.min(...tijden);
    const max = Math.max(...tijden);
    segmenten.push(t('trainingHistory.cardioTijd', {
      waarde: min === max ? tijdLabel(min) : `${tijdLabel(min)}-${tijdLabel(max)}`,
    }));
  }

  const snelheden = sets.map(s => num(s.speed_value)).filter(v => v !== null);
  if (snelheden.length > 0) segmenten.push(t('trainingHistory.cardioSnelheid', { waarde: bereik(snelheden) }));

  const hellingen = sets.map(s => num(s.incline_percent)).filter(v => v !== null);
  if (hellingen.length > 0) segmenten.push(t('trainingHistory.cardioHelling', { waarde: bereik(hellingen) }));

  const weerstanden = sets.map(s => num(s.resistance_level)).filter(v => v !== null);
  if (weerstanden.length > 0) segmenten.push(t('trainingHistory.cardioWeerstand', { waarde: bereik(weerstanden) }));

  const hartslagen = sets.map(s => num(s.heart_rate_avg)).filter(v => v !== null);
  if (hartslagen.length > 0) segmenten.push(t('trainingHistory.cardioHartslag', { waarde: bereik(hartslagen) }));

  const calorieen = sets.map(s => num(s.calories)).filter(v => v !== null);
  if (calorieen.length > 0) segmenten.push(t('trainingHistory.cardioKcal', { waarde: bereik(calorieen) }));

  return segmenten.length > 0
    ? segmenten.join(' · ')
    : t(sets.length === 1 ? 'trainingHistory.oefeningSetsZonderRepsEnkel' : 'trainingHistory.oefeningSetsZonderReps', { sets: sets.length });
}

// Het type waarop de weergave dispatcht: altijd eerst het snapshot op de
// sessie-oefening (review M11, migratie 20260815170000), pas daarna de live
// waarde uit de bibliotheek. Zonder dat snapshot veranderde de weergave van
// álle historische sessies zodra een beheerder een oefening omkatte van
// cardio naar kracht: de opgeslagen afstand/tijd/kcal verdwenen dan uit
// beeld terwijl ze gewoon in workout_sets stonden. De fallback dekt rijen
// van vóór de migratie waarvan het snapshot niet te vullen was.
export function typeVanOefening(oefening) {
  return oefening?.exercise_type || oefening?.exercises?.exercise_type || 'kracht';
}

// Eén samenvattingsregel voor een oefening, kracht of cardio.
export function vatOefeningSamen(oefening) {
  if (!oefening?.sets?.length) return '';
  return typeVanOefening(oefening) === 'cardio'
    ? vatCardioSetsSamen(oefening.sets)
    : vatSetsSamen(oefening.sets);
}

// Elke oefening die in de meegegeven sessies voorkomt, nieuwste eerst. De
// sessies worden verondersteld al op datum aflopend gesorteerd te zijn.
export function oefeningKeuzes(sessies) {
  const gezien = new Map();
  for (const sessie of sessies) {
    for (const oefening of oefeningenVan(sessie)) {
      const info = oefening.exercises;
      if (info?.id && !gezien.has(info.id)) gezien.set(info.id, info.name);
    }
  }
  return [...gezien].map(([id, naam]) => ({ id, naam }));
}

// Topset = de zwaarste set in de meest recente sessie waarin de gekozen
// oefening voorkomt; bij gelijk gewicht wint de set met de meeste reps.
export function bepaalTopset(sessies, exerciseId) {
  if (!exerciseId) return null;
  for (const sessie of sessies) {
    for (const oefening of oefeningenVan(sessie)) {
      if (oefening.exercises?.id !== exerciseId || oefening.sets.length === 0) continue;
      const beste = oefening.sets.reduce((a, b) => {
        const gewichtA = num(a.weight) ?? -1;
        const gewichtB = num(b.weight) ?? -1;
        if (gewichtB !== gewichtA) return gewichtB > gewichtA ? b : a;
        return (num(b.reps) ?? -1) > (num(a.reps) ?? -1) ? b : a;
      });
      return {
        gewicht: num(beste.weight),
        reps: num(beste.reps),
        datum: sessieDatum(sessie),
        naam: oefening.exercises?.name || '',
      };
    }
  }
  return null;
}

// De topset als leesbare waarde. null = niets te tonen (kaart valt terug op
// de neutrale lege staat in plaats van "NaN kg × null").
export function topsetLabel(topset) {
  if (!topset) return null;
  if (topset.gewicht === null) {
    return topset.reps === null
      ? null
      : t('trainingHistory.statTopsetZonderGewicht', { reps: formatGetal(topset.reps) });
  }
  const gewicht = formatGetal(topset.gewicht);
  return topset.reps === null
    ? t('trainingHistory.statTopsetGewichtZonderReps', { gewicht })
    : t('trainingHistory.statTopsetGewicht', { gewicht, reps: formatGetal(topset.reps) });
}

// ─────────────────────────────────────────────────────────────────────────
// Onderdeel 1 — de trainingsdatum is `performed_on`, niet het moment waarop
// de registratie technisch werd afgerond. `completed_at` blijft de fallback
// voor rijen van vóór migratie 20260817100000 (die zijn weliswaar
// gebackfild, maar een lezer die dit veld niet meeselecteert mag geen lege
// datum tonen).
export function sessieDatum(sessie) {
  return sessie?.performed_on || sessie?.completed_at || sessie?.created_at || null;
}

// ─────────────────────────────────────────────────────────────────────────
// Onderdeel 7 — "Gelogd door ...".
//
// De oorzaak van "Gelogd door onbekend": de weergave leunde uitsluitend op
// `coach_id` + de coachnamenlijst. Een zelfgelogde sessie heeft per definitie
// `coach_id IS NULL` (de RLS dwingt dat af sinds review M12), dus die viel
// altijd door naar "onbekend" — terwijl de bron juist volledig bekend is.
//
// Vanaf nu is `logged_by` het uitgangspunt en is "onbekend" gereserveerd
// voor het enige geval waarin de bron werkelijk niet vast te stellen is.
export function loggerLabel(sessie, coachNamen = {}, clientNaam = null) {
  if (sessie?.logged_by === 'client') {
    return clientNaam
      ? t('trainingHistory.gelogdDoor', { naam: clientNaam })
      : t('trainingHistory.gelogdDoorClient');
  }
  if (sessie?.logged_by === 'coach') {
    const naam = sessie.coach_id ? coachNamen[sessie.coach_id] : null;
    return naam
      ? t('trainingHistory.gelogdDoor', { naam })
      : t('trainingHistory.gelogdDoorCoach');
  }
  return t('trainingHistory.gelogdDoorOnbekend');
}

// Gemiddelde RPE over één sessie, alleen over sets waar RPE echt is
// ingevuld. Zonder die sets null, zodat de kaart leeg blijft i.p.v. NaN.
export function gemiddeldeRpe(sessie) {
  if (!sessie) return null;
  const waarden = oefeningenVan(sessie)
    .flatMap(oefening => oefening.sets.map(s => num(s.rpe)))
    .filter(v => v !== null);
  if (waarden.length === 0) return null;
  return {
    gemiddelde: waarden.reduce((a, b) => a + b, 0) / waarden.length,
    aantal: waarden.length,
  };
}
